import React, { useState, useEffect } from 'react';
import { getDistributorByIdAPI } from '../../../api/distributors';

export interface DistributorAddress {
  id?: number;
  addressType?: string;
  addressLine1?: string;
  addressLine2?: string;
  pincode?: string;
  city?: {
    id?: number;
    name?: string;
    territory?: {
      id?: number;
      name?: string;
      zone?: {
        id?: number;
        name?: string;
      };
    };
  };
  route?: {
    id?: number;
    name?: string;
    depo?: {
      id?: number;
      name?: string;
      sapName?: string;
    };
  };
}

export interface WalletBalance {
  id?: number;
  balance?: number;
  creditLimit?: number;
  currentBalance?:number;
}

export interface DistributorType {
  id?: number;
  name?: string;
}

export interface DistributorDetailData {
  id?: number | string;
  loginId?: string;
  sapCode?: string;
  sapCustomerId?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  alternatePhone?: string | null;
  paymentTerms?: string | null;
  gst?: string | null;
  panNo?: string | null;
  fassiNo?: string | null;
  status?: string;
  distributorType?: DistributorType;
  walletBalances?: WalletBalance[];
  addresses?: DistributorAddress[];
}

interface ViewDistributorDetailsProps {
  activeDist: DistributorDetailData & Record<string, any>;
}

export const ViewDistributorDetails: React.FC<ViewDistributorDetailsProps> = ({ activeDist }) => {
  const [details, setDetails] = useState<DistributorDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const distId = activeDist?.id;

  useEffect(() => {
    let isMounted = true;
    const fetchDetails = async () => {
      if (!distId) return;
      try {
        setLoading(true);
        const res = await getDistributorByIdAPI(distId);
        if (!isMounted) return;
        const apiData = res?.data || res?.distributor || (res?.status === 'success' ? res.data : null);
        if (apiData) {
          setDetails(apiData);
        }
      } catch (err) {
        console.warn('Could not fetch distributor details from API:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [distId]);

  // Combined data strictly from API response or activeDist passed from navigation
  const companyName = details?.companyName || details?.firstName || activeDist?.companyName || activeDist?.name || '-';
  const idValue = details?.id || activeDist?.id || '-';
  const loginId = details?.loginId || activeDist?.loginId || '-';
  const sapCode = details?.sapCode || details?.sapCustomerId || activeDist?.sapCode || activeDist?.sapCustomerId || activeDist?.code || '-';
  const email = details?.email || activeDist?.email || '-';
  const phone = details?.phone || details?.alternatePhone || activeDist?.phone || '-';
  const paymentTerms = details?.paymentTerms || activeDist?.paymentTerms || '-';
  const gst = details?.gst || activeDist?.gst || '-';
  const panNo = details?.panNo || activeDist?.panNo || '-';
  const fassiNo = details?.fassiNo || activeDist?.fassiNo || '-';
  const statusRaw = details?.status || activeDist?.status || '-';
  const isStatusActive = String(statusRaw).toUpperCase() === 'ACTIVE' || String(statusRaw) === '1' || (statusRaw as any) === true;
  const distributorTypeName =
    details?.distributorType?.name ||
    (typeof activeDist?.distributorType === 'string'
      ? activeDist.distributorType
      : activeDist?.distributorType?.name) ||
    '-';

  // Wallet balances strictly from API or activeDist
  const walletList = (details?.walletBalances && details.walletBalances.length > 0)
    ? details.walletBalances
    : (activeDist?.walletBalances && activeDist.walletBalances.length > 0)
    ? activeDist.walletBalances
    : [];

  const wallet = walletList[0];

  // Addresses strictly from API or activeDist
  const addressList: DistributorAddress[] =
    details?.addresses && details.addresses.length > 0
      ? details.addresses
      : activeDist?.addresses && activeDist.addresses.length > 0
      ? activeDist.addresses
      : activeDist?.address || activeDist?.city || activeDist?.territory || activeDist?.zone || activeDist?.route || activeDist?.depo
      ? [
          {
            addressLine1: activeDist?.address,
            city: {
              name: activeDist?.city,
              territory: {
                name: activeDist?.territory,
                zone: {
                  name: activeDist?.zone,
                },
              },
            },
            route: {
              name: activeDist?.route,
              depo: {
                name: activeDist?.depo,
              },
            },
          },
        ]
      : [];

  const firstAddress = addressList[0];
  const fullLocQuery = [
    firstAddress?.addressLine1,
    firstAddress?.addressLine2,
    firstAddress?.city?.name,
    firstAddress?.pincode,
  ]
    .filter(Boolean)
    .join(', ');

  const handleGoogleMapsClick = () => {
    if (!fullLocQuery && companyName === '-') return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullLocQuery || companyName)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl border border-[#ECECF2] p-8 text-center select-none">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#C1272D] border-t-transparent mb-3"></div>
        <p className="text-xs font-semibold text-slate-500">Loading distributor details...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-[#ECECF2] p-6 shadow-sm select-none space-y-6">
      {/* Header inside Card: Distributor Name and Status */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-100">
        <div>
          <h4 className="text-xl font-extrabold text-[#C1272D] m-0 tracking-tight">
            {companyName}
          </h4>
          <p className="text-sm text-slate-500 font-semibold mt-1">SAP Code: {sapCode} {loginId !== '-' ? `| Login ID: ${loginId}` : ''}</p>
        </div>
        <span
          className={`px-4 py-1.5 text-xs font-bold rounded-full select-none ${
            isStatusActive ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-rose-100 text-rose-700 border border-rose-200'
          }`}
        >
          {isStatusActive ? 'ACTIVE' : 'INACTIVE'}
        </span>
      </div>

      {/* Section 1: Distributor Details */}
      <div className="space-y-4">
        <h5 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
          Distributor Details
        </h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-5">
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Distributor ID</span>
            <span className="text-sm font-bold text-slate-800">{idValue}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">SAP Code</span>
            <span className="text-sm font-bold text-slate-800">{sapCode}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Login ID</span>
            <span className="text-sm font-bold text-slate-800">{loginId}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Distributor Type</span>
            <span className="text-sm font-bold text-slate-800">{distributorTypeName}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Payment Terms</span>
            <span className="text-sm font-bold text-slate-800">{paymentTerms}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Phone</span>
            <span className="text-sm font-bold text-slate-800">{phone}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">Email</span>
            <span className="text-sm font-bold text-slate-800">{email}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">GST No.</span>
            <span className="text-sm font-bold text-slate-800">{gst}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">PAN No.</span>
            <span className="text-sm font-bold text-slate-800">{panNo}</span>
          </div>
          <div>
            <span className="text-slate-500 text-sm font-semibold block mb-0.5">FSSAI No.</span>
            <span className="text-sm font-bold text-slate-800">{fassiNo}</span>
          </div>
        </div>
      </div>

      {/* Section 2: Wallet & Credit Limits */}
      <div className="space-y-4">
        <h5 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
          Wallet Balances & Credit
        </h5>
        {wallet ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-emerald-50/60 border border-emerald-100 p-4 rounded-2xl">
              <span className="text-emerald-700 text-sm font-semibold block mb-0.5">Wallet Balance</span>
              <span className="text-lg font-extrabold text-emerald-800">
                {wallet?.currentBalance !== undefined && wallet?.currentBalance !== null
                  ? `₹${Number(wallet.currentBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                  : '-'}
              </span>
            </div>
            <div className="bg-amber-50/60 border border-amber-100 p-4 rounded-2xl">
              <span className="text-amber-800 text-sm font-semibold block mb-0.5">Credit Limit</span>
              <span className="text-lg font-extrabold text-amber-900">
                {wallet?.creditLimit !== undefined && wallet?.creditLimit !== null
                  ? `₹${Number(wallet.creditLimit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                  : '-'}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 font-medium py-1">No active wallet balance record found.</p>
        )}
      </div>

      {/* Section 3: Associated Addresses & Location */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <h5 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">
            Address & Location Details
          </h5>
          {(fullLocQuery || companyName !== '-') && (
            <button
              onClick={handleGoogleMapsClick}
              className="text-xs font-bold text-[#C1272D] hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>View on Google Maps</span>
            </button>
          )}
        </div>

        {addressList.length === 0 ? (
          <p className="text-sm text-slate-400 font-medium py-2">No address details available.</p>
        ) : (
          addressList.map((addr, idx) => (
            <div key={addr.id || idx} className="bg-slate-50 border border-slate-200/70 p-5 rounded-2xl space-y-4">
              {/* Address Header & Lines 1 and 2 */}
              <div>
                <span className="text-slate-500 text-sm font-semibold block mb-1">Address</span>
                <div className="text-sm font-bold text-slate-800 space-y-1">
                  <div>1) {addr.addressLine1 || '-'}</div>
                  {addr.addressLine2 && addr.addressLine2 !== '-' ? (
                    <div>2) {addr.addressLine2}</div>
                  ) : null}
                </div>
              </div>

              {/* Location Fields in sequence: Pincode, Zone, Territory, City, Depo, Route */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-3 border-t border-slate-200/60">
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">Pincode</span>
                  <span className="text-sm font-bold text-slate-800">{addr.pincode || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">Zone</span>
                  <span className="text-sm font-bold text-slate-800">{addr.city?.territory?.zone?.name || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">Territory</span>
                  <span className="text-sm font-bold text-slate-800">{addr.city?.territory?.name || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">City</span>
                  <span className="text-sm font-bold text-slate-800">{addr.city?.name || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">Depo</span>
                  <span className="text-sm font-bold text-slate-800">{addr.route?.depo?.name || addr.route?.depo?.sapName || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-sm font-semibold block mb-0.5">Route</span>
                  <span className="text-sm font-bold text-slate-800">{addr.route?.name || '-'}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ViewDistributorDetails;
